import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera,
  Upload,
  Loader2,
  ArrowLeft,
  Sparkles,
  Receipt as ReceiptIcon,
  ScrollText,
  ArrowRight,
  Banknote,
  FileSpreadsheet,
  FileText,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  Bills,
  Receipts,
  OCR,
} from "@/lib/api";
import { filesToOcrPayload, firstImageOf } from "@/lib/multi-file";

const TYPE_META = {
  bill: { label: "Bill", icon: ScrollText, hint: "Money owed — Billy will save it as a bill." },
  receipt: { label: "Receipt", icon: ReceiptIcon, hint: "Money spent — Billy will save it as a receipt." },
  bank_transaction: { label: "Bank Transaction", icon: Banknote, hint: "Money moved — Billy will help you allocate it to a bill." },
  quote: { label: "Quote", icon: FileSpreadsheet, hint: "New job — Billy will create 3 deposit incomes (50/30/20%)." },
  unknown: { label: "Not sure", icon: Sparkles, hint: "Pick one below." },
};

const SmartCapture = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [payload, setPayload] = useState(null); // {images:[...], previews:[...]}
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [override, setOverride] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    try {
      const p = await filesToOcrPayload(fileList);
      setPayload(p);
      setResult(null);
      setOverride(null);
      await classify(p);
    } catch (e) {
      toast.error(e.message || "Could not read the files.");
    }
  };

  const classify = async (p) => {
    setScanning(true);
    try {
      const r = await OCR.smartCapture({ images: p.images });
      setResult(r);
      const t = r.type;
      if (t === "unknown") {
        toast.message("Billy isn't sure — pick the type below.");
      } else {
        toast.success(`Looks like a ${TYPE_META[t]?.label.toLowerCase() || t}.`);
      }
    } catch {
      toast.error("Couldn't read the upload. Try again or pick the type manually.");
    } finally {
      setScanning(false);
    }
  };

  const decidedType = override || result?.type || "unknown";

  const saveAsBill = async () => {
    const b = result?.bill || {};
    const firstImg = firstImageOf(payload) || {};
    if (!b.company || !b.invoice_number || !b.amount_due || !b.due_date) {
      navigate("/new", { state: { prefill: b, image: firstImg, allImages: payload?.images } });
      return;
    }
    setSaving(true);
    try {
      const created = await Bills.create({
        ...b,
        amount_due: Number(b.amount_due),
        notes: b.notes || "",
        image_base64: firstImg.image_base64,
        image_mime: firstImg.image_mime,
        bill_type: "single",
      });
      toast.success("Bill saved.");
      navigate(`/bills/${created.id || created.first_bill_id}`);
    } catch (err) {
      navigate("/new", { state: { prefill: b, image: firstImg, allImages: payload?.images } });
    } finally {
      setSaving(false);
    }
  };

  const saveAsReceipt = async () => {
    const r = result?.receipt || {};
    const firstImg = firstImageOf(payload) || {};
    if (!r.vendor || !r.total_amount || !r.receipt_date) {
      navigate("/receipts/new", { state: { prefill: r, image: firstImg } });
      return;
    }
    setSaving(true);
    try {
      const created = await Receipts.create({
        ...r,
        total_amount: Number(r.total_amount),
        gst_amount: Number(r.gst_amount) || 0,
        category: r.category || "Other",
        notes: r.notes || "",
        image_base64: firstImg.image_base64,
        image_mime: firstImg.image_mime,
      });
      toast.success("Receipt saved.");
      navigate(`/receipts/${created.id}`);
    } catch {
      navigate("/receipts/new", { state: { prefill: r, image: firstImg } });
    } finally {
      setSaving(false);
    }
  };

  const goAllocate = () => {
    navigate("/allocate", { state: { tx: result?.bank_transaction || {} } });
  };

  const goQuote = () => {
    navigate("/income/from-quote", { state: { quote: result?.quote || {} } });
  };

  const onSave = () => {
    if (decidedType === "bill") return saveAsBill();
    if (decidedType === "receipt") return saveAsReceipt();
    if (decidedType === "bank_transaction") return goAllocate();
    if (decidedType === "quote") return goQuote();
    toast.error("Pick what this document is.");
  };

  const reset = () => {
    setPayload(null);
    setResult(null);
    setOverride(null);
  };

  const TypeIcon = TYPE_META[decidedType]?.icon || Sparkles;
  const meta = TYPE_META[decidedType] || TYPE_META.unknown;

  const ctaLabel =
    decidedType === "bill"
      ? "Save Bill"
      : decidedType === "receipt"
        ? "Save Receipt"
        : decidedType === "bank_transaction"
          ? "Allocate to Bill"
          : decidedType === "quote"
            ? "Create 3 Incomes"
            : "";

  return (
    <div data-testid="smart-capture" className="fade-rise">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black mb-6"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="swiss-label mb-3">Smart Capture</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
        Snap anything
      </h1>
      <p className="text-neutral-600 mt-3 max-w-2xl">
        Bills, receipts, bank transactions, or quotes — Billy works it out. Upload one
        photo, multiple pages, or a PDF — AI reads them all in one go.
      </p>

      <div className="mt-10">
        {!payload ? (
          <div className="dropzone">
            <div className="flex flex-col items-center gap-6">
              <div className="h-16 w-16 border-2 border-black flex items-center justify-center">
                <Camera className="h-7 w-7" strokeWidth={2} />
              </div>
              <div className="text-center">
                <div className="font-display font-extrabold text-2xl">
                  Snap or upload
                </div>
                <div className="text-neutral-500 text-sm mt-1">
                  JPG, PNG, WEBP, or PDF. Multiple files / pages welcome.
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  data-testid="take-photo-btn"
                  onClick={() => cameraInputRef.current?.click()}
                  className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
                >
                  <Camera className="h-4 w-4" /> Take Photo
                </button>
                <button
                  data-testid="upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" /> Upload Files / PDF
                </button>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              data-testid="file-input"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              data-testid="camera-input"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
            <div>
              <FilePreviewGrid previews={payload.previews} />
              <div className="flex flex-wrap gap-3 mt-4">
                <button
                  onClick={reset}
                  data-testid="reset-btn"
                  className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-xs px-4 py-2 hover:bg-black hover:text-white transition-colors"
                >
                  Replace
                </button>
                <button
                  onClick={() => classify(payload)}
                  disabled={scanning}
                  data-testid="rerun-btn"
                  className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-xs px-4 py-2 hover:bg-black hover:text-white transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {scanning ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  Re-scan
                </button>
              </div>
            </div>

            <div className="space-y-5">
              {scanning && (
                <div className="text-sm text-neutral-600 inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Billy is reading
                  {payload.images.length > 1 ? ` all ${payload.images.length} pages…` : " it…"}
                </div>
              )}
              {result && (
                <>
                  <div className="border-2 border-black p-5 bg-white">
                    <div className="swiss-label">Billy's call</div>
                    <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                      <div className="font-display font-black text-3xl tracking-tight inline-flex items-center gap-2">
                        <TypeIcon className="h-7 w-7" />
                        {meta.label}
                      </div>
                      {result.confidence > 0 && !override && (
                        <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
                          {Math.round(result.confidence * 100)}% confident
                        </div>
                      )}
                    </div>
                    <div className="text-sm text-neutral-600 mt-2">{meta.hint}</div>
                    <div className="grid grid-cols-2 gap-2 mt-5">
                      <TypeBtn icon={ScrollText} label="Bill" sub="Track + remind" testid="pick-bill" active={decidedType === "bill"} onClick={() => setOverride("bill")} />
                      <TypeBtn icon={ReceiptIcon} label="Receipt" sub="Categorise for BAS" testid="pick-receipt" active={decidedType === "receipt"} onClick={() => setOverride("receipt")} />
                      <TypeBtn icon={Banknote} label="Bank Transaction" sub="Allocate to bill" testid="pick-bank" active={decidedType === "bank_transaction"} onClick={() => setOverride("bank_transaction")} />
                      <TypeBtn icon={FileSpreadsheet} label="Quote" sub="3-stage incomes" testid="pick-quote" active={decidedType === "quote"} onClick={() => setOverride("quote")} />
                    </div>
                  </div>

                  {decidedType === "bill" && <Preview title="Extracted bill fields" rows={[["Company", result.bill?.company], ["Invoice #", result.bill?.invoice_number], ["Amount due", result.bill?.amount_due], ["Due date", result.bill?.due_date], ["BSB / Acc", [result.bill?.bsb, result.bill?.account_number].filter(Boolean).join(" · ")], ["BPAY", [result.bill?.bpay_biller_code, result.bill?.bpay_reference].filter(Boolean).join(" / ")]]} />}
                  {decidedType === "receipt" && <Preview title="Extracted receipt fields" rows={[["Vendor", result.receipt?.vendor], ["Date", result.receipt?.receipt_date], ["Total", result.receipt?.total_amount], ["GST", result.receipt?.gst_amount], ["Method", result.receipt?.payment_method], ["Category", result.receipt?.category]]} />}
                  {decidedType === "bank_transaction" && <Preview title="Extracted transaction" rows={[["Amount", result.bank_transaction?.amount], ["Date", result.bank_transaction?.transaction_date], ["Description", result.bank_transaction?.description], ["Reference", result.bank_transaction?.reference], ["Counterparty", result.bank_transaction?.counterparty], ["Direction", result.bank_transaction?.direction]]} />}
                  {decidedType === "quote" && <Preview title="Extracted quote" rows={[["Customer", result.quote?.customer_name], ["Quote #", result.quote?.quote_number], ["Total", result.quote?.total_amount], ["Quote date", result.quote?.quote_date], ["Install date", result.quote?.install_date], ["Notes", result.quote?.notes]]} />}

                  <button
                    data-testid="save-btn"
                    onClick={onSave}
                    disabled={saving || decidedType === "unknown"}
                    className="w-full border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-5 py-4 hover:bg-white hover:text-black transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                    {ctaLabel}
                  </button>
                  <div className="text-[11px] text-neutral-500">
                    Missing fields? Billy will open the manual form pre-filled so you can finish.
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TypeBtn = ({ icon: Icon, label, sub, active, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`p-3 border-2 transition-colors text-left ${
      active
        ? "bg-black text-white border-black"
        : "bg-white text-black border-neutral-300 hover:border-black"
    }`}
  >
    <div className="flex items-center gap-2 mb-1">
      <Icon className="h-4 w-4" />
      <span className="font-bold uppercase tracking-widest text-[10px]">{label}</span>
    </div>
    <div className="text-[11px] opacity-70">{sub}</div>
  </button>
);

const FilePreviewGrid = ({ previews }) => (
  <div className="grid grid-cols-3 gap-2">
    {previews.map((p, i) => (
      <div
        key={i}
        data-testid={`capture-preview-${i}`}
        className="border-2 border-black bg-neutral-100 aspect-square overflow-hidden flex items-center justify-center"
      >
        {p.previewUrl ? (
          <img src={p.previewUrl} alt={p.name} className="object-cover w-full h-full" />
        ) : (
          <div className="text-center p-2">
            <FileText className="h-8 w-8 mx-auto" />
            <div className="text-[10px] uppercase tracking-widest font-bold mt-1">
              PDF
            </div>
            <div className="text-[10px] text-neutral-500 mt-0.5 break-all">
              {p.name}
            </div>
          </div>
        )}
      </div>
    ))}
  </div>
);

const Preview = ({ title, rows }) => (
  <div className="border-2 border-black p-5 bg-white">
    <div className="swiss-label mb-3">{title}</div>
    <dl className="space-y-2 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-baseline justify-between gap-3">
          <dt className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{k}</dt>
          <dd className="font-bold mono text-right break-all">
            {v != null && v !== "" ? String(v) : "—"}
          </dd>
        </div>
      ))}
    </dl>
  </div>
);

export default SmartCapture;
